import { makeAutoObservable } from "mobx";

enum ValidationMessages {
  required = "Required",
}

type ValidationRule = {
  check: (v: unknown) => boolean;
  message?: ValidationMessages | string;
};

export type ValidationConfig = Record<
  string,
  {
    rules: ValidationRule[];
  }
>;

type Errors = Record<string, string>;

export class Form<T extends object> {
  private readonly _values: T;

  private _errors: Errors = {};

  private readonly _config: ValidationConfig;

  constructor(values: T, config: ValidationConfig = {}) {
    this._values = values;
    this._config = config;
    makeAutoObservable(this);
  }

  get values(): T {
    return this._values;
  }

  getValue<K extends keyof T & string>(field: K): T[K] {
    return this._values[field];
  }

  private get errors(): Errors {
    return this._errors;
  }

  private set errors(errors: Errors) {
    this._errors = errors;
  }

  getErrors = (): Errors => this._errors;

  onChange<K extends keyof T & string>(field: K, value: T[K]): void {
    this._values[field] = value;
    if (this._config[field]) {
      this._errors[field] = "";
    }
  }

  onMultipleChange(fields: Array<{ [K in keyof T & string]: { field: K; value: T[K] } }[keyof T & string]>): void {
    fields.forEach(({ field, value }) => {
      this.onChange(field, value);
    });
  }

  getError(field: string): string | undefined {
    return this._errors[field];
  }

  private readonly _checkField = (field: string, value: unknown): string => {
    const rules = this._config[field]?.rules ?? [];
    const failed = rules
      .map(({ check, message }) => ({
        isValid: check(value),
        message: message ?? ValidationMessages.required,
      }))
      .find((result) => !result.isValid);
    return failed?.message ?? "";
  };

  isFieldValid(field: string, value: unknown): boolean {
    const errors: Errors = { ...this.errors };
    if (this._config[field]) {
      errors[field] = this._checkField(field, value);
    }
    this.errors = errors;

    return !this.errors[field];
  }

  get hasErrors(): boolean {
    return Object.values(this.errors).some((e) => e !== "");
  }

  isValid(): boolean {
    Object.keys(this._config).forEach((field) => {
      const value: unknown = Reflect.get(this._values, field);
      this.isFieldValid(field, value);
    });
    return !this.hasErrors;
  }
}
